import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';

const Cart = () => {
  const navigate = useNavigate();
  const { user, token, cart, setCart } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQuantity = item.quantity + delta;
        return newQuantity > 0 ? { ...item, quantity: newQuantity } : item;
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeItem = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
    toast.success('Removed from cart');
  };

  const calculateTotal = () => {
    return cart.reduce((sum, item) => sum + (item.product.price * item.quantity), 0);
  };

  const handleCheckout = async () => {
    if (!user) {
      toast.error('Please login to checkout');
      navigate('/auth');
      return;
    }

    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      // Create order
      const orderItems = cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity
      }));
      
      const orderResponse = await axios.post(`${API}/orders`, orderItems, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Create checkout session
      const checkoutResponse = await axios.post(
        `${API}/payments/checkout`,
        {
          order_id: orderResponse.data.id,
          origin_url: window.location.origin
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Redirect to Stripe
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error('Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-12">
        <h1 className="text-5xl font-bold text-center mb-12 bg-gradient-to-r from-teal-500 to-pink-500 bg-clip-text text-transparent">
          Shopping Cart
        </h1>
        
        {cart.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-cart">
            <p className="text-gray-500 text-lg mb-6">Your cart is empty</p>
            <Button 
              onClick={() => navigate('/shop')}
              className="bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 rounded-full"
            >
              Continue Shopping
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {cart.map((item) => (
                <Card key={item.product_id} data-testid={`cart-item-${item.product_id}`}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <img 
                        src={item.product.image_url}
                        alt={item.product.title}
                        className="w-24 h-24 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{item.product.title}</h3>
                        <p className="text-gray-600 text-sm mb-2">{item.product.description}</p>
                        <p className="text-teal-600 font-bold">${item.product.price.toFixed(2)}</p>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(item.product_id)}
                          data-testid="remove-item-btn"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.product_id, -1)}
                            data-testid="decrease-quantity-btn"
                          >
                            -
                          </Button>
                          <span className="w-8 text-center" data-testid="item-quantity">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.product_id, 1)}
                            data-testid="increase-quantity-btn"
                          >
                            +
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <div>
              <Card className="sticky top-4">
                <CardContent className="p-6">
                  <h2 className="text-2xl font-bold mb-4">Order Summary</h2>
                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between text-gray-600">
                      <span>Subtotal</span>
                      <span data-testid="cart-subtotal">${calculateTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Shipping</span>
                      <span>Free</span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between text-xl font-bold">
                        <span>Total</span>
                        <span data-testid="cart-total">${calculateTotal().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  <Button 
                    onClick={handleCheckout}
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-teal-500 to-pink-500 hover:from-teal-600 hover:to-pink-600 text-white py-6 text-lg rounded-full"
                    data-testid="checkout-btn"
                  >
                    {loading ? 'Processing...' : 'Proceed to Checkout'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;